import React, { useState } from 'react';
import {
  X, Globe, Server, ExternalLink,
  Copy, Check, Eye, EyeOff, RefreshCw, ShieldCheck, ShieldAlert, AlertCircle, FileText, FilePlus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WhatsAppIcon = ({ size = 16, style = {} }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="currentColor"
    viewBox="0 0 24 24"
    style={style}
  >
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.513 2.262 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.73-1.458L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.625 1.451 5.432.002 9.851-4.414 9.855-9.843.002-2.63-1.023-5.101-2.886-6.968-1.863-1.867-4.347-2.894-6.98-2.896-5.433 0-9.854 4.417-9.858 9.848-.002 1.81.488 3.579 1.419 5.141L1.896 22.15l6.3-1.652zM17.07 14.67c-.276-.138-1.636-.807-1.89-.899-.253-.093-.437-.138-.621.138-.184.276-.713.899-.874 1.084-.16.184-.321.207-.597.069-.276-.138-1.168-.43-2.226-1.374-.823-.733-1.378-1.64-1.539-1.916-.16-.276-.017-.425.12-.562.124-.124.276-.322.414-.483.138-.161.184-.276.276-.46.092-.184.046-.345-.023-.483-.069-.138-.621-1.496-.85-2.05-.224-.54-.449-.465-.621-.474-.16-.008-.344-.01-.528-.01-.184 0-.483.069-.736.345-.253.276-.966.943-.966 2.3 0 1.357.989 2.668 1.127 2.852.138.184 1.947 2.972 4.717 4.164.659.284 1.173.453 1.574.58.662.21 1.265.18 1.741.11.531-.078 1.636-.669 1.868-1.313.23-.643.23-1.196.16-1.312-.069-.115-.253-.184-.53-.322z" />
  </svg>
);

export default function DomainDetailModal({ isOpen, onClose, domain, onRenew, onEdit }) {
  const [showDomainPassword, setShowDomainPassword] = useState(false);
  const [showHostingPassword, setShowHostingPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [isRenewing, setIsRenewing] = useState(false);
  const navigate = useNavigate();

  if (!isOpen || !domain) return null;

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleRenew = async () => {
    setIsRenewing(true);
    await onRenew(domain.id);
    setIsRenewing(false);
  };

  const days = domain.daysRemaining;

  const domainUrl = domain.domainLoginUrl || domain.loginUrl || '';
  const domainUser = domain.domainUsername || domain.username || '';
  const domainPass = domain.domainPassword || domain.password || '';

  const hostingUrl = domain.hostingLoginUrl || domain.loginUrl || '';
  const hostingUser = domain.hostingUsername || domain.username || '';
  const hostingPass = domain.hostingPassword || domain.password || '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '720px' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8fafc'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Globe size={24} color="#4f46e5" />
              <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#0f172a' }}>
                {domain.domainName}
              </h2>
            </div>
            {domain.category && (
              <span style={{ fontSize: '0.75rem', color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: '6px', marginTop: '4px', display: 'inline-block', fontWeight: '600' }}>
                {domain.category}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={() => { onClose(); navigate('/billing'); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px' }}>
              <FilePlus size={14} /> Invoice
            </button>
            <button className="btn btn-secondary" onClick={() => { onClose(); navigate('/proforma'); }} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px' }}>
              <FilePlus size={14} /> Proforma
            </button>
            <button className="btn btn-secondary" onClick={() => { onClose(); onEdit(domain); }}>
              Edit
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '24px' }}>
          {/* Renewal Status Banner */}
          <div style={{
            background: days <= 0 ? '#fef2f2' : days <= 7 ? '#fff7ed' : '#ecfdf5',
            border: `1px solid ${days <= 0 ? '#fecaca' : days <= 7 ? '#ffedd5' : '#a7f3d0'}`,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {days <= 0 ? <AlertCircle size={24} color="#dc2626" /> : days <= 7 ? <ShieldAlert size={24} color="#c2410c" /> : <ShieldCheck size={24} color="#047857" />}
              <div>
                <div style={{ fontWeight: '700', color: days <= 0 ? '#b91c1c' : days <= 7 ? '#c2410c' : '#047857', fontSize: '0.95rem' }}>
                  {days <= 0 ? 'Domain Renewal Expired!' : days <= 7 ? 'Critical Expiry Notice!' : 'Domain Status Active'}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Renewal Date: <strong style={{ color: '#0f172a' }}>{domain.renewalDate}</strong> ({days <= 0 ? `${Math.abs(days)} days ago` : `${days} days remaining`})
                </div>
              </div>
            </div>

            <button
              className="btn btn-renew"
              onClick={handleRenew}
              disabled={isRenewing}
              style={{ padding: '8px 14px', fontSize: '0.85rem' }}
            >
              <RefreshCw size={14} className={isRenewing ? 'spin' : ''} />
              <span>{isRenewing ? 'Renewing...' : 'Renew (+1 yr)'}</span>
            </button>
          </div>

          {/* Grid Information */}
          <div className="modal-grid-2" style={{ marginBottom: '20px' }}>
            {/* Registrar & Hosting */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '4px' }}>Domain Registrar</div>
              <div style={{ fontSize  : '1rem', fontWeight: '700', color: '#0f172a', marginBottom: '14px' }}>{domain.registrar || 'N/A'}</div>

              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '4px' }}>Hosting Platform</div>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a' }}>{domain.hostingProvider || 'N/A'}</div>
            </div>

            {/* Financial & Renewal Settings */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '4px' }}>Renewal Cost</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#059669', marginBottom: '14px' }}>
                ₹{domain.cost || 0} <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', fontWeight: 'normal' }}>/{domain.billingCycle}</span>
              </div>

              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '4px' }}>Auto-Renew Setting</div>
              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: domain.autoRenew ? '#059669' : '#dc2626' }}>
                {domain.autoRenew ? 'Enabled (Auto Charges Registrar)' : 'Disabled (Manual Renewal Required)'}
              </div>
            </div>
          </div>

          {/* Client Information */}
          {(domain.clientName || domain.whatsappNo) && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '18px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div>
                <h4 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#16a34a', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <WhatsAppIcon size={18} /> Client Information
                </h4>
                <div style={{ display: 'flex', gap: '24px' }}>
                  {domain.clientName && (
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block' }}>Client Name</span>
                      <strong style={{ color: '#0f172a', fontSize: '0.9rem' }}>{domain.clientName}</strong>
                    </div>
                  )}
                  {domain.whatsappNo && (
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block' }}>WhatsApp Contact</span>
                      <strong style={{ color: '#0f172a', fontSize: '0.9rem' }}>{domain.whatsappNo}</strong>
                    </div>
                  )}
                </div>
              </div>
              {domain.whatsappNo && (
                <a
                  href={`https://wa.me/${domain.whatsappNo.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi, regarding your domain ${domain.domainName}...`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-renew"
                  style={{ padding: '8px 16px', fontSize: '0.82rem', background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', boxShadow: '0 4px 14px rgba(37,211,102,0.3)' }}
                >
                  <WhatsAppIcon size={14} /> Start Chat
                </a>
              )}
            </div>
          )}

          {/* Website Developer / Made By Information */}
          {domain.madeBy && (
            <div style={{
              background: '#faf5ff',
              border: '1px solid #e9d5ff',
              borderRadius: '12px',
              padding: '18px',
              marginBottom: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
              <h4 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#7c3aed', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                🛠️ Developer Information
              </h4>
              <div style={{ display: 'flex', gap: '24px', marginTop: '4px' }}>
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'block' }}>Website Created / Managed By</span>
                  <strong style={{ color: '#581c87', fontSize: '0.92rem' }}>{domain.madeBy}</strong>
                </div>
              </div>
            </div>
          )}

          {/* Section 1: Separate Domain Registrar Credentials */}
          <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '18px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Globe size={18} color="#4338ca" />
                <h4 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>
                  Domain Registrar Login ({domain.registrar || 'Domain Portal'})
                </h4>
              </div>
              {domainUrl && (
                <a
                  href={domainUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '0.78rem' }}
                >
                  <ExternalLink size={12} /> Open Registrar Portal
                </a>
              )}
            </div>

            <div className="modal-grid-2">
              {/* Username */}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Username / Email</label>
                <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px' }}>
                  <input
                    type="text"
                    readOnly
                    value={domainUser || 'Not set'}
                    style={{ background: 'none', border: 'none', color: '#0f172a', width: '100%', outline: 'none', fontSize: '0.88rem' }}
                  />
                  {domainUser && (
                    <button
                      onClick={() => copyToClipboard(domainUser, 'domainUsername')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}
                    >
                      {copiedField === 'domainUsername' ? <Check size={14} color="#059669" /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Password / PIN</label>
                <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px' }}>
                  <input
                    type={showDomainPassword ? 'text' : 'password'}
                    readOnly
                    value={domainPass || 'Not set'}
                    style={{ background: 'none', border: 'none', color: '#0f172a', width: '100%', outline: 'none', fontSize: '0.88rem' }}
                  />
                  {domainPass && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button
                        onClick={() => setShowDomainPassword(!showDomainPassword)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}
                      >
                        {showDomainPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(domainPass, 'domainPassword')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}
                      >
                        {copiedField === 'domainPassword' ? <Check size={14} color="#059669" /> : <Copy size={14} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Separate Hosting Platform Credentials */}
          <div style={{ background: '#ecfeff', border: '1px solid #cff4fc', borderRadius: '12px', padding: '18px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Server size={18} color="#0891b2" />
                <h4 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>
                  Hosting Platform Login ({domain.hostingProvider || 'Hosting Panel'})
                </h4>
              </div>
              {hostingUrl && (
                <a
                  href={hostingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '0.78rem' }}
                >
                  <ExternalLink size={12} /> Open Hosting Panel
                </a>
              )}
            </div>

            <div className="modal-grid-2">
              {/* Username */}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Username / Email</label>
                <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px' }}>
                  <input
                    type="text"
                    readOnly
                    value={hostingUser || 'Not set'}
                    style={{ background: 'none', border: 'none', color: '#0f172a', width: '100%', outline: 'none', fontSize: '0.88rem' }}
                  />
                  {hostingUser && (
                    <button
                      onClick={() => copyToClipboard(hostingUser, 'hostingUsername')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}
                    >
                      {copiedField === 'hostingUsername' ? <Check size={14} color="#059669" /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Password / PIN</label>
                <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px' }}>
                  <input
                    type={showHostingPassword ? 'text' : 'password'}
                    readOnly
                    value={hostingPass || 'Not set'}
                    style={{ background: 'none', border: 'none', color: '#0f172a', width: '100%', outline: 'none', fontSize: '0.88rem' }}
                  />
                  {hostingPass && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button
                        onClick={() => setShowHostingPassword(!showHostingPassword)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}
                      >
                        {showHostingPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(hostingPass, 'hostingPassword')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}
                      >
                        {copiedField === 'hostingPassword' ? <Check size={14} color="#059669" /> : <Copy size={14} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Technical Specs */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
            <h4 style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Server size={14} /> Server & DNS Configuration
            </h4>
            <div className="modal-grid-2-1" style={{ fontSize: '0.85rem' }}>
              <div>
                <span style={{ color: 'var(--text-dim)' }}>IP Address:</span>
                <div style={{ fontWeight: '600', color: '#0f172a', fontFamily: 'monospace' }}>{domain.ipAddress || '—'}</div>
              </div>
              <div>
                <span style={{ color: 'var(--text-dim)' }}>Nameservers:</span>
                <div style={{ fontWeight: '600', color: '#0f172a', fontFamily: 'monospace' }}>
                  {Array.isArray(domain.nameServers) && domain.nameServers.length > 0
                    ? domain.nameServers.join(', ')
                    : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {domain.notes && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FileText size={14} /> Notes & Remarks
              </div>
              <p style={{ fontSize: '0.88rem', color: '#334155', lineHeight: '1.5' }}>
                {domain.notes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
